// src/components/Home.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchOrders, getRestaurantProfile } from "../../utils/api";
import AuthContext from "../../contexts/AuthContext";
import { motion } from "framer-motion";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function HomeScreen() {
  const { restaurantId, loading: authLoading, error: authError } =
    useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurantName, setRestaurantName] = useState("");
  const [filterDate, setFilterDate] = useState([
    new Date(new Date().getFullYear(), 0, 1),
    new Date(),
  ]);
  const [isRealTime, setIsRealTime] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const refreshIntervalRef = useRef(null);

  const filterStart = filterDate[0];
  const filterEnd = filterDate[1];

  // same-day & today detection
  const isSameDayFilter =
    filterStart &&
    filterEnd &&
    filterStart.toDateString() === filterEnd.toDateString() &&
    filterStart.toDateString() === new Date().toDateString();

  // Fetch restaurant profile
  useEffect(() => {
    if (!restaurantId || authLoading) return;
    getRestaurantProfile(restaurantId)
      .then((p) => setRestaurantName(p.name || "My Restaurant"))
      .catch(() => setRestaurantName("My Restaurant"));
  }, [restaurantId, authLoading]);

  // fetch orders function (re-usable)
  const fetchOrdersData = async () => {
    if (!restaurantId || authLoading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrders(
        restaurantId,
        filterStart?.toISOString(),
        filterEnd?.toISOString()
      );
      setOrders(data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Home: fetch orders failed", err);
      setError("Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // initial + filter driven fetch
  useEffect(() => {
    if (!restaurantId || authLoading) return;
    fetchOrdersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, filterStart?.toISOString(), filterEnd?.toISOString(), authLoading]);

  // real-time polling when today is selected
  useEffect(() => {
    if (isSameDayFilter && restaurantId && !authLoading) {
      setIsRealTime(true);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = setInterval(fetchOrdersData, 30000); // 30s
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      setIsRealTime(false);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameDayFilter, restaurantId, authLoading]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, []);

  // Loading / error / auth states
  if (authLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-sm text-gray-500">Authenticating…</div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-sm text-red-500">{authError}</div>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-sm text-gray-500">
          No restaurant found for this user.
        </div>
      </div>
    );
  }

  // Metrics derived from orders
  const pendingOrders = orders.filter(
    (o) => (o.status || "").toLowerCase() === "pending"
  );
  const completedOrders = orders.filter(
    (o) => (o.status || "").toLowerCase() === "completed"
  );
  const pendingRevenue = pendingOrders.reduce((s, o) => s + (o.total || 0) * 1.05, 0);
  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0) * 1.05, 0);
  const totalOrders = orders.length;
  const completedOrdersCount = completedOrders.length;

  const completionPercentage = totalOrders > 0 ? Math.round((completedOrdersCount / totalOrders) * 100) : 0;
  const targetPercentage = Math.min(100, Math.round((totalRevenue / 20000) * 100));

  // Charts data
  const ordersByDay = Array(7).fill(0);
  const revenueByMonth = Array(12).fill(0);
  const paymentMethods = { Cash: 0, Card: 0, UPI: 0 };

  orders.forEach((o) => {
    const ts = o.createdAt?._seconds || o.createdAt?.seconds || o.createdAt || null;
    let d;
    if (typeof ts === "number") d = new Date(ts * 1000);
    else if (ts && ts.toDate) d = ts.toDate();
    else if (o.createdAt) d = new Date(o.createdAt);
    else return;
    ordersByDay[d.getDay()]++;
    revenueByMonth[d.getMonth()] += (o.total || 0) * 1.05;

    const pm = (o.paymentMethod || "").toLowerCase();
    if (pm.includes("cash")) paymentMethods.Cash++;
    else if (pm.includes("card")) paymentMethods.Card++;
    else paymentMethods.UPI++;
  });

  const orderTrend = {
    labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    datasets: [
      {
        label: "Orders",
        data: ordersByDay,
        borderColor: "#f43f5e",
        backgroundColor: "rgba(244,63,94,0.15)",
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
    ],
  };
  const chartData = {
    orderTrend: {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      datasets: [
        {
          label: "Orders",
          data: ordersByDay,
          borderColor: "#f472b6",
          backgroundColor: "rgba(244,114,182,0.2)",
          tension: 0.4,
          fill: true,
        },
      ],
    },
    revenueTrend: {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets: [
        {
          label: "Revenue",
          data: revenueByMonth,
          borderColor: "#60a5fa",
          backgroundColor: "rgba(96,165,250,0.2)",
          tension: 0.4,
          fill: true,
        },
      ],
    },
    customerMap: {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      datasets: [
        {
          label: "Orders",
          data: ordersByDay,
          backgroundColor: "#fbbf24",
        },
      ],
    },
  };

  const revenueTrend = {
    labels: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
    datasets: [
      {
        label: "Revenue (₹)",
        data: revenueByMonth,
        backgroundColor: "rgba(99,102,241,0.9)",
      },
    ],
  };

  const paymentPie = {
    labels: Object.keys(paymentMethods),
    datasets: [
      {
        label: "Payments",
        data: Object.values(paymentMethods),
        backgroundColor: ["#6366f1", "#fb923c", "#10b981"],
      },
    ],
  };

  const formatDateRange = () => {
    if (!filterStart || !filterEnd) return "Select Date Range";
    if (filterStart.toDateString() === filterEnd.toDateString()) {
      return filterStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    return `${filterStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${filterEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };

  const handleManualRefresh = () => fetchOrdersData();

  // nice color gradients for summary
  const summaryCards = [
    { label: "Total Orders", value: totalOrders.toLocaleString(), gradient: "from-purple-500 via-pink-500 to-rose-400", icon: "📊" },
    { label: "Total Revenue", value: `₹${Math.round(totalRevenue).toLocaleString()}`, gradient: "from-emerald-400 via-teal-500 to-cyan-600", icon: "💰" },
    { label: "Pending Revenue", value: `₹${Math.round(pendingRevenue).toLocaleString()}`, gradient: "from-amber-400 via-orange-500 to-rose-500", icon: "⏳" },
  ];
  const pieCharts = [
    {
      label: "Order Completion",
      value: totalOrders
        ? Math.round((completedOrdersCount / totalOrders) * 100)
        : 0,
      color: "#f87171",
      icon: "🧾",
    },
    {
      label: "Pending Orders",
      value: totalOrders
        ? Math.round((pendingOrders.length / totalOrders) * 100)
        : 0,
      color: "#34d399",
      icon: "👥",
    },
    {
      label: "Revenue Target",
      value: totalRevenue
        ? Math.min(100, Math.round((totalRevenue / 20000) * 100))
        : 0,
      color: "#60a5fa",
      icon: "💸",
    },
  ];

  
  return (
    <div className="w-full h-full bg-gray-50 text-gray-900 flex flex-col ">
      {/* improved global scrollbar & smooth scrolling styles to remove colored right bar */}
      <style>{`
        /* make smooth scrolling for the container */
        .mobile-scroll-container {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          scrollbar-width: thin;
          scrollbar-color: rgba(0,0,0,0.12) transparent;
        }
        .mobile-scroll-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
          background: transparent;
        }
        .mobile-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.12);
          border-radius: 999px;
        }
        .mobile-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }

        /* also hide any colored scrollbar coming from parent document (applies only while this component is mounted) */
        html::-webkit-scrollbar, body::-webkit-scrollbar, #root::-webkit-scrollbar {
          width: 8px;
          background: transparent;
        }
        html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb, #root::-webkit-scrollbar-thumb {
          background: transparent !important;
        }
        html, body, #root {
          scrollbar-color: transparent transparent;
        }

        /* ensure react-datepicker popper appears above nav */
        .react-datepicker-popper { z-index: 9999; }
      `}</style>

      {/* Header - gradient */}
      <div className="px-4 pt-4 pb-3" style={{ background: "linear-gradient(90deg,#ffe5f0 0%, #ffd7b5 50%, #e8fdf5 100%) " }}>
        <div className="flex items-center justify-between ">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{restaurantName}</h1>
          </div>
        </div>

        {isRealTime && (
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-green-700 font-medium">Live Updates Active</span>
            <span className="text-xs text-gray-600">Last: {lastRefresh.toLocaleTimeString()}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <DatePicker
              selectsRange
              startDate={filterDate[0]}
              endDate={filterDate[1]}
              onChange={(update) => {
                // update can be null, single date, or [start, end]
                if (!update) {
                  setFilterDate([new Date(new Date().getFullYear(), 0, 1), new Date()]);
                } else {
                  // if user clicks a single date, DatePicker sometimes passes a Date instance - handle both array and single
                  if (Array.isArray(update)) setFilterDate(update);
                  else setFilterDate([update, update]);
                }
              }}
              dateFormat="MMM d, yyyy"
              className="border border-gray-200 rounded-lg px-3 py-2 bg-white shadow-sm text-sm focus:outline-none"
              isClearable={true}
              placeholderText="Select date range"
              maxDate={new Date()}
              calendarClassName="shadow-lg rounded-lg border-0"
              popperClassName="shadow-xl"
              popperPlacement="bottom-end"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-3 overflow-x-auto">
          <button
            className="px-3 py-1 rounded-lg bg-white text-xs text-gray-700 font-semibold shadow-sm"
            onClick={() => setFilterDate([new Date(new Date().getFullYear(), 0, 1), new Date()])}
          >
            All Time
          </button>
          <button
            className="px-3 py-1 rounded-lg bg-white text-xs text-gray-700 font-semibold shadow-sm"
            onClick={() => setFilterDate([new Date(), new Date()])}
          >
            Today
          </button>
          <button
            onClick={handleManualRefresh}
            className="px-3 py-1 rounded-lg bg-white text-xs text-gray-700 font-semibold shadow-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-28 mobile-scroll-container">
        {loading ? (
          <div className="py-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-red-600">{error}</div>
        ) : (
          <>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 gap-3">
                {summaryCards.map((s) => (
                  <div key={s.label} className={`rounded-xl p-4 text-white shadow-lg`} style={{ background: `linear-gradient(90deg, var(--start, #6b21a8), var(--mid, #ec4899), var(--end, #fb7185))` }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs opacity-90">{s.label}</div>
                        <div className="text-2xl font-extrabold mt-1">{s.value}</div>
                      </div>
                      <div className="text-3xl opacity-90">{s.icon}</div>
                    </div>
                  </div>
                ))}
                <style>{`
                  .grid .rounded-xl:nth-child(1) { background: linear-gradient(90deg,#7c3aed,#ec4899); }
                  .grid .rounded-xl:nth-child(2) { background: linear-gradient(90deg,#10b981,#06b6d4); }
                  .grid .rounded-xl:nth-child(3) { background: linear-gradient(90deg,#fb923c,#ef4444); }
                `}</style>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 col-span-1"
            >
              <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">
                Quick Stats
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {pieCharts.map((pie, index) => (
                  <motion.div
                    key={pie.label}
                    whileHover={{ scale: 1.05 }}
                    className="flex flex-col items-center"
                  >
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                      <Pie
                        data={{
                          labels: [pie.label],
                          datasets: [
                            {
                              data: [pie.value, 100 - pie.value],
                              backgroundColor: [pie.color, "#f3f4f6"],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          plugins: { legend: { display: false } },
                          cutout: "70%",
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center flex-col">
                        <span className="text-lg sm:text-2xl">{pie.icon}</span>
                        <span className="text-xs font-semibold">
                          {pie.value}%
                        </span>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-600 mt-1 sm:mt-2 text-center">
                      {pie.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
              </div>
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6"
            >
              <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">
                Customer Traffic
              </h3>
              <Bar
                data={chartData.customerMap}
                options={{
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: "#f3f4f6" },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                height={150}
              />
            </motion.div>

            {/* Order Trend Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:col-span-2"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 gap-2">
                <h3 className="font-semibold text-base sm:text-lg text-gray-800">
                  Order Trend
                </h3>
                
              </div>
              <Line
                data={chartData.orderTrend}
                options={{
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: "#1f2937",
                      titleColor: "#f9fafb",
                      bodyColor: "#f9fafb",
                      borderColor: "#4b5563",
                      borderWidth: 1,
                      padding: 10,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: "#f3f4f6" },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                height={120}
              />
            </motion.div>
              
              
            </div>
          </>
        )}
        <div className="h-15" />
      </div>
      
      
          </div>

    
  );
}